import type { ColumnFiltersState, Table } from '@tanstack/react-table'
import dayjs from 'dayjs'
import type { LucideIcon } from 'lucide-react'
import { CircleIcon } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { DataTableFilter, useDataTableFilters } from '../data-table-filter'
import { createColumnConfigHelper } from '../data-table-filter/core/filters'
import type {
  ColumnConfig,
  ColumnDataType,
  ColumnOption,
  FiltersState,
} from '../data-table-filter/core/types'
import type { DataTableFilterField } from './types'

const PlaceholderIcon: LucideIcon = CircleIcon

interface DataTableFilterAdapterProps<TData> {
  table: Table<TData>
  filterFields: DataTableFilterField<TData>[]
  data: TData[]
  strategy: 'client' | 'server'
  locale?: 'en'
}

const BOOLEAN_OPTIONS: ColumnOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
]

// biome-ignore lint/suspicious/noExplicitAny: Generic cache for filter fields
const helperCache = new WeakMap<DataTableFilterField<any>[], ColumnConfig<any, ColumnDataType>[]>()

const buildColumnConfigs = <TData,>(
  fields: DataTableFilterField<TData>[],
): ColumnConfig<TData, ColumnDataType>[] => {
  // biome-ignore lint/suspicious/noExplicitAny: Generic type cast for cache key
  const cached = helperCache.get(fields as DataTableFilterField<any>[])
  if (cached) return cached

  const helper = createColumnConfigHelper<TData>()
  const configs: ColumnConfig<TData, ColumnDataType>[] = []

  for (const field of fields) {
    if (field.isBoolean) {
      const builder = helper
        .boolean()
        .id(String(field.value))
        .accessor((row) => {
          const value = row[field.value]
          if (typeof value === 'boolean') return value
          if (typeof value === 'string') {
            const normalized = value.toLowerCase()
            if (normalized === 'true') return true
            if (normalized === 'false') return false
          }
          if (value === 1 || value === '1') return true
          if (value === 0 || value === '0') return false
          return Boolean(value)
        })
        .displayName(field.label)
        .icon(PlaceholderIcon)
        .showInMoreFilters(field.showInMoreFilters)

      if (field.pluralLabel) builder.pluralLabel(field.pluralLabel)

      configs.push(builder.build() as ColumnConfig<TData, ColumnDataType>)
      continue
    }

    if (field.remoteOptionsConfig) {
      const isMulti = field.multiSelect ?? true
      const builder = isMulti ? helper.multiOption() : helper.option()
      const builtBuilder = builder
        .id(String(field.value))
        .accessor((row) => row[field.value])
        .displayName(field.label)
        .icon(PlaceholderIcon)
        .showInMoreFilters(field.showInMoreFilters)

      const finalBuilder = field.pluralLabel
        ? builtBuilder.pluralLabel(field.pluralLabel)
        : builtBuilder
      const config = finalBuilder.build() as ColumnConfig<TData, ColumnDataType>
      // Include static options if provided (e.g., "Main Account" alongside remote subaccounts)
      if (field.options?.length) {
        // biome-ignore lint/suspicious/noExplicitAny: Static options for remote columns
        ;(config as any).options = field.options.map(({ label, labelString, value, icon }) => ({
          label,
          labelString,
          value,
          icon,
        }))
      }
      // Attach remote options config for the filter controller to use
      // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
      ;(config as any).remoteOptionsConfig = field.remoteOptionsConfig
      configs.push(config)
      continue
    }

    if (field.options?.length) {
      const isMulti = field.multiSelect ?? true
      const builder = isMulti ? helper.multiOption() : helper.option()
      const builtBuilder = builder
        .id(String(field.value))
        .accessor((row) => row[field.value])
        .displayName(field.label)
        .icon(PlaceholderIcon)
        .showInMoreFilters(field.showInMoreFilters)

      const finalBuilder = field.pluralLabel
        ? builtBuilder.pluralLabel(field.pluralLabel)
        : builtBuilder
      configs.push(finalBuilder.build() as ColumnConfig<TData, ColumnDataType>)
      continue
    }

    if (field.isDurationRange) {
      let builder = helper
        .durationRange()
        .id(String(field.value))
        .accessor((row) => row[field.value] as unknown as number)
        .displayName(field.label)
        .icon(PlaceholderIcon)
        .showInMoreFilters(field.showInMoreFilters)

      if (field.pluralLabel) builder = builder.pluralLabel(field.pluralLabel)
      if (field.durationRangeOptions) {
        builder = builder.durationRangeOptions(field.durationRangeOptions)
      }

      configs.push(builder.build() as ColumnConfig<TData, ColumnDataType>)
      continue
    }

    if (field.isDateRange) {
      let builder = helper
        .date()
        .id(String(field.value))
        .accessor((row) => row[field.value] as unknown as Date)
        .displayName(field.label)
        .icon(PlaceholderIcon)
        .showInMoreFilters(field.showInMoreFilters)

      if (field.pluralLabel) builder = builder.pluralLabel(field.pluralLabel)
      if (field.dateRangeOptions) {
        builder = builder.dateRangeOptions(field.dateRangeOptions)
      }

      configs.push(builder.build() as ColumnConfig<TData, ColumnDataType>)
      continue
    }

    let builder = helper
      .text()
      .id(String(field.value))
      .accessor((row) => row[field.value] as unknown as string)
      .displayName(field.label)
      .icon(PlaceholderIcon)
      .showInMoreFilters(field.showInMoreFilters)

    if (field.placeholder) builder = builder.placeholder(field.placeholder)
    if (field.pluralLabel) builder = builder.pluralLabel(field.pluralLabel)

    configs.push(builder.build() as ColumnConfig<TData, ColumnDataType>)
  }

  // biome-ignore lint/suspicious/noExplicitAny: Generic type cast for cache key
  helperCache.set(fields as DataTableFilterField<any>[], configs)
  return configs
}

const columnFiltersToFiltersState = <TData,>(
  filters: ColumnFiltersState,
  fields: DataTableFilterField<TData>[],
): FiltersState =>
  filters.reduce<FiltersState>((acc, current) => {
    const field = fields.find((candidate) => String(candidate.value) === current.id)
    if (!field) return acc

    if (field.isBoolean) {
      const value = Array.isArray(current.value) ? current.value[0] : current.value
      const boolValue = value === 'true' || value === true

      acc.push({
        columnId: current.id,
        type: 'boolean',
        operator: 'is',
        values: [boolValue],
      })
      return acc
    }

    if (field.options?.length || field.remoteOptionsConfig) {
      const values = Array.isArray(current.value)
        ? (current.value as string[])
        : current.value?.toString().split(',').filter(Boolean)

      if (!values?.length) return acc

      const isMultiSelect = field.multiSelect ?? true

      acc.push({
        columnId: current.id,
        type: isMultiSelect ? 'multiOption' : 'option',
        operator: isMultiSelect ? 'include any of' : 'is',
        values,
      })
      return acc
    }

    if (field.isDurationRange && typeof current.value === 'string') {
      const durationValues = current.value.split(',').filter(Boolean).map(Number)
      if (durationValues.length < 2) return acc

      acc.push({
        columnId: current.id,
        type: 'durationRange',
        operator: 'is between',
        values: durationValues,
      })
      return acc
    }

    if (field.isDateRange && typeof current.value === 'string') {
      const dateValues = current.value.split(',').filter(Boolean)
      if (!dateValues.length) return acc

      acc.push({
        columnId: current.id,
        type: 'date',
        operator: dateValues.length > 1 ? 'is between' : 'is',
        values: dateValues,
      })
      return acc
    }

    if (typeof current.value === 'string' && current.value.trim().length > 0) {
      acc.push({
        columnId: current.id,
        type: 'text',
        operator: 'contains',
        values: [current.value],
      })
    }

    return acc
  }, [])

const filtersStateToColumnFilters = (
  filters: FiltersState,
  managedIds: Set<string>,
  existing: ColumnFiltersState,
): ColumnFiltersState => {
  const managed = filters.map((filter) => {
    let value: string | string[]

    if (filter.type === 'durationRange' && Array.isArray(filter.values)) {
      value = filter.values.map(String).join(',')
    } else if (filter.type === 'date' && Array.isArray(filter.values)) {
      value = filter.values.map((date) => dayjs(date as Date).format('YYYY-MM-DD')).join(',')
    } else if (filter.type === 'multiOption' || filter.type === 'option') {
      value = filter.values as string[]
    } else if (Array.isArray(filter.values) && filter.values.length > 1) {
      value = filter.values.map(String).join(',')
    } else if (Array.isArray(filter.values)) {
      value = String(filter.values[0] ?? '')
    } else {
      value = String(filter.values)
    }

    return {
      id: filter.columnId,
      value,
    }
  })

  const preserved = existing.filter((filter) => !managedIds.has(filter.id))
  return [...preserved, ...managed]
}

// biome-ignore lint/suspicious/noExplicitAny: Generic filter field utility
const normalizeOptions = (field: DataTableFilterField<any>): ColumnOption[] | undefined => {
  if (field.options?.length) {
    return field.options.map(({ label, labelString, value, icon }) => ({
      label,
      labelString,
      value,
      icon,
    }))
  }

  if (field.isBoolean) {
    return BOOLEAN_OPTIONS
  }

  return undefined
}

export const DataTableFilterAdapter = <TData,>({
  table,
  filterFields,
  data,
  strategy,
  locale = 'en',
}: DataTableFilterAdapterProps<TData>) => {
  const previousFiltersRef = useRef<string>('')
  const columnConfigs = useMemo(() => buildColumnConfigs(filterFields), [filterFields])

  const columnOptions = useMemo(() => {
    return filterFields.reduce<Record<string, ColumnOption[] | undefined>>((acc, field) => {
      const options = normalizeOptions(field)
      if (options?.length) {
        acc[String(field.value)] = options
      }
      return acc
    }, {})
  }, [filterFields])

  const columnIds = useMemo(() => columnConfigs.map((config) => config.id), [columnConfigs])

  const { columns, filters, actions } = useDataTableFilters({
    strategy,
    data,
    columnsConfig: columnConfigs,
    defaultFilters: columnFiltersToFiltersState(table.getState().columnFilters, filterFields),
    options: columnOptions,
  })

  const serializedFilters = JSON.stringify(filters)

  useEffect(() => {
    if (previousFiltersRef.current === serializedFilters) return
    previousFiltersRef.current = serializedFilters

    const managedIds = new Set(columnIds)
    const existingFilters = table.getState().columnFilters
    const nextFilters = filtersStateToColumnFilters(filters, managedIds, existingFilters)

    table.setColumnFilters(nextFilters)
  }, [filters, serializedFilters, table, columnIds])

  if (!columnConfigs.length) return null

  const enhancedActions = {
    ...actions,
    removeAllFilters: () => {
      actions.removeAllFilters()
      table.resetColumnFilters()
    },
  }

  return (
    <DataTableFilter
      columns={columns}
      filters={filters}
      actions={enhancedActions}
      strategy={strategy}
      locale={locale}
    />
  )
}
